import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { BACKEND_CONFIG } from '../../utils/productionConfig';

const API_URL = BACKEND_CONFIG.apiUrl;

interface Profile {
  _id: string;
  userId: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    isPhoneVerified: boolean;
    isEmailVerified: boolean;
    subscription?: any;
  };
  gender: string;
  dateOfBirth: string;
  religion: string;
  community: string;
  maritalStatus: string;
  height: number;
  location: {
    city: string;
    state: string;
    country: string;
  };
  education: {
    highestDegree: string;
    college: string;
  };
  career: {
    occupation: string;
    company: string;
    annualIncome: number;
  };
  about: string;
  photos: string[];
  isSponsored?: boolean;
  createdAt: string;
}

interface ProfileFilters {
  page?: number;
  limit?: number;
  gender?: string;
  minAge?: number;
  maxAge?: number;
  religion?: string;
  community?: string;
  caste?: string;
  motherTongue?: string;
  city?: string;
  state?: string;
  country?: string;
  maritalStatus?: string;
  education?: string;
  educationLevel?: string;
  occupation?: string;
  annualIncome?: string;
  minHeight?: number;
  maxHeight?: number;
  search?: string;
  filterType?: 'all' | 'new' | 'premium' | 'nearby' | 'verified' | 'recently-active';
}

interface DiscoveryState {
  profiles: Profile[];
  loading: boolean;
  backgroundLoading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  filters: ProfileFilters;
  lastFetched: number;
  cacheExpiry: number;
  shouldFetchMore: boolean;
}

const initialState: DiscoveryState = {
  profiles: [],
  loading: false,
  backgroundLoading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  },
  filters: {
    page: 1,
    limit: 20
  },
  lastFetched: 0,
  cacheExpiry: 30 * 60 * 1000, // 30 minutes cache for better UX
  shouldFetchMore: false
};

export const fetchDiscoveryProfiles = createAsyncThunk(
  'discovery/fetchProfiles',
  async ({ filters, isBackground = false }: { filters?: ProfileFilters, isBackground?: boolean }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return rejectWithValue('No authentication token');
      }

      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const params = new URLSearchParams();
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            params.append(key, value.toString());
          }
        });
      }

      const url = `${API_URL}/profiles?${params.toString()}`;
      
      // Development-only debug logging
      if (process.env.NODE_ENV === 'development') {
        console.log('🌐 Discovery API Request:');
        console.log('   - URL:', url);
        console.log('   - Filters:', filters);
        console.log('   - Params:', params.toString());
      }

      const { data } = await axios.get(url);

      const profiles = Array.isArray(data?.data?.profiles) ? data.data.profiles : [];
      const pagination = data?.data?.pagination || {
        page: 1,
        limit: 20,
        total: 0,
        pages: 0
      };

      // Development-only debug logging
      if (process.env.NODE_ENV === 'development') {
        console.log('📊 Discovery API Response:');
        console.log('   - Profiles count:', profiles.length);
        console.log('   - Pagination:', pagination);
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        console.log('📋 FIRST 3 PROFILES FROM API:');
        profiles.slice(0, 3).forEach((profile: any, index: number) => {
          console.log(`Profile ${index + 1}:`, {
            _id: profile._id,
            id: profile.id,
            userId: profile.userId,
            firstName: profile.firstName,
            fullProfile: profile
          });
        });
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      }

      return { profiles, pagination, isBackground };
    } catch (error: any) {
      // Only log to console in development, don't show error UI
      if (process.env.NODE_ENV === 'development') {
        console.warn('⚠️ Discovery API Error (non-critical):');
        if (error.message === 'Network Error') {
          console.warn('   - Network connection issue. Will retry later.');
        } else {
          console.warn('   - Message:', error.response?.data?.message || error.message);
          console.warn('   - Status:', error.response?.status);
        }
      }
      
      // Return empty result instead of rejecting to prevent UI errors
      return { 
        profiles: [], 
        pagination: { page: 1, limit: 20, total: 0, pages: 0 },
        isBackground,
        error: error.response?.data?.message || error.message || 'Failed to fetch profiles'
      };
    }
  }
);

// 🔥 OPTIMIZATION: Better cache handling to prevent reload on back navigation
export const discoveryPersistConfig = {
  key: 'discovery',
  storage: localStorage,
  whitelist: ['profiles', 'pagination', 'lastFetched', 'filters'] // Only persist these fields
};

const discoverySlice = createSlice({
  name: 'discovery',
  initialState,
  reducers: {
    updateFilters: (state, action: PayloadAction<Partial<ProfileFilters>>) => {
      // Development logging
      if (process.env.NODE_ENV === 'development') {
        console.log('🔧 Redux updateFilters:');
        console.log('   - Current filters:', state.filters);
        console.log('   - New filters payload:', action.payload);
      }
      
      // Check if this is a reset (empty object or only has page/limit)
      const payloadKeys = Object.keys(action.payload).filter(k => k !== 'page' && k !== 'limit');
      const isReset = payloadKeys.length === 0;
      
      if (isReset) {
        // Complete reset - clear all filters except page and limit
        if (process.env.NODE_ENV === 'development') {
          console.log('   - Action: RESET - Clearing all filters');
        }
        state.filters = {
          page: 1,
          limit: state.filters.limit || 20
        };
      } else {
        // Merge new filters
        if (process.env.NODE_ENV === 'development') {
          console.log('   - Action: MERGE - Adding/updating filters');
        }
        state.filters = { ...state.filters, ...action.payload, page: 1 };
      }
      
      state.lastFetched = 0; // Invalidate cache on filter change
      
      if (process.env.NODE_ENV === 'development') {
        console.log('   - Result filters:', state.filters);
      }
    },
    resetAllFilters: (state) => {
      // Complete reset action
      if (process.env.NODE_ENV === 'development') {
        console.log('🔧 Redux resetAllFilters called');
      }
      state.filters = {
        page: 1,
        limit: 20
      };
      state.lastFetched = 0;
    },
    nextPage: (state) => {
      if (state.pagination.page < state.pagination.pages) {
        state.filters.page = (state.filters.page || 1) + 1;
        state.lastFetched = 0; // Invalidate cache on page change
      }
    },
    prevPage: (state) => {
      if (state.pagination.page > 1) {
        state.filters.page = (state.filters.page || 1) - 1;
        state.lastFetched = 0; // Invalidate cache on page change
      }
    },
    goToPage: (state, action: PayloadAction<number>) => {
      if (action.payload >= 1 && action.payload <= state.pagination.pages) {
        state.filters.page = action.payload;
        state.lastFetched = 0; // Invalidate cache on page change
      }
    },
    clearCache: (state) => {
      state.lastFetched = 0;
    },
    removeProfile: (state, action: PayloadAction<string>) => {
      state.profiles = state.profiles.filter(p => p._id !== action.payload);
      state.pagination.total = Math.max(0, state.pagination.total - 1);
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDiscoveryProfiles.pending, (state, action) => {
        // Don't show loading if we have valid cached data
        const hasCachedData = state.profiles.length > 0 && 
                              (Date.now() - state.lastFetched) < state.cacheExpiry;
        
        if (action.meta.arg.isBackground || hasCachedData) {
          state.backgroundLoading = true;
        } else {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchDiscoveryProfiles.fulfilled, (state, action) => {
        // Always update profiles - even if empty (filters might have matched nothing)
        state.profiles = action.payload.profiles;
        state.pagination = action.payload.pagination;
        state.loading = false;
        state.backgroundLoading = false;
        state.error = null;
        state.lastFetched = Date.now();
        
        // Development-only debug logging
        if (process.env.NODE_ENV === 'development') {
          console.log('📊 Discovery Fulfilled:');
          console.log('   - Profiles received:', action.payload.profiles.length);
          console.log('   - Current filters:', state.filters);
        }
      })
      .addCase(fetchDiscoveryProfiles.rejected, (state, action) => {
        state.loading = false;
        state.backgroundLoading = false;
        state.error = action.payload as string;
      });
  }
});

export const { updateFilters, resetAllFilters, nextPage, prevPage, goToPage, clearCache, removeProfile } = discoverySlice.actions;
export default discoverySlice.reducer;